<template>
  <v-container fluid class="wiki">
    <div class="wiki-shell">
      <header class="wiki-header">
        <div class="wiki-heading">
          <div class="wiki-heading-icon" aria-hidden="true">
            <v-icon size="24" color="#fff">mdi-book-open-page-variant</v-icon>
          </div>
          <div class="wiki-heading-copy">
            <div class="wiki-eyebrow">Baza wiedzy BTP</div>
            <h1>Wiki</h1>
            <p>Instrukcje, procedury i pliki zespołu w jednym uporządkowanym miejscu.</p>
          </div>
        </div>

        <div v-if="canManage" class="wiki-header-actions">
          <button type="button" class="wiki-action wiki-action--secondary" @click="openCreateSection">
            <v-icon size="17">mdi-folder-plus-outline</v-icon>
            <span>{{ $t('wiki.newSection') }}</span>
          </button>
          <button type="button" class="wiki-action wiki-action--secondary" @click="openCreateNote">
            <v-icon size="17">mdi-note-plus-outline</v-icon>
            <span>Nowa notatka</span>
          </button>
          <button type="button" class="wiki-action wiki-action--primary" @click="openCreateArticle">
            <v-icon size="17">mdi-plus</v-icon>
            <span>{{ $t('wiki.addArticle') }}</span>
          </button>
        </div>
      </header>

      <section v-if="currentSection !== '__files'" class="wiki-command" aria-label="Wyszukiwanie i podsumowanie bazy wiedzy">
        <div class="wiki-search-wrap" :class="{ 'has-value': query }">
          <span class="wiki-search-icon" aria-hidden="true">
            <v-icon size="21">mdi-magnify</v-icon>
          </span>
          <span class="wiki-search-copy">
            <small>Szybkie wyszukiwanie</small>
            <input
              v-model="query"
              type="search"
              class="wiki-search-input"
              :placeholder="$t('wiki.searchArticle')"
              :aria-label="$t('wiki.searchArticle')"
            >
          </span>
          <button v-if="query" type="button" class="wiki-search-clear" aria-label="Wyczyść wyszukiwanie" @click.prevent="query = ''">
            <v-icon size="17">mdi-close</v-icon>
          </button>
          <span class="wiki-search-shortcut" aria-hidden="true">{{ filteredArticles.length }} wyników</span>
        </div>

        <div class="wiki-stats" aria-label="Podsumowanie Wiki">
          <div class="wiki-stat">
            <span class="wiki-stat-icon wiki-stat-icon--blue"><v-icon size="18">mdi-file-document-multiple-outline</v-icon></span>
            <div><strong>{{ articles.length }}</strong><small>artykułów</small></div>
          </div>
          <div class="wiki-stat">
            <span class="wiki-stat-icon wiki-stat-icon--purple"><v-icon size="18">mdi-shape-outline</v-icon></span>
            <div><strong>{{ sections.length }}</strong><small>sekcji</small></div>
          </div>
          <div class="wiki-stat">
            <span class="wiki-stat-icon wiki-stat-icon--green"><v-icon size="18">mdi-paperclip</v-icon></span>
            <div><strong>{{ attachmentTotal }}</strong><small>załączników</small></div>
          </div>
        </div>
      </section>

      <div class="wiki-workspace">
        <aside class="wiki-navigation" aria-label="Sekcje Wiki">
          <div class="wiki-nav-head">
            <div>
              <span>Obszary wiedzy</span>
              <strong>Przeglądaj sekcje</strong>
            </div>
            <span class="wiki-nav-total">{{ sections.length }}</span>
          </div>

          <div class="wiki-nav-list">
            <button
              type="button"
              class="wiki-nav-item"
              :class="{ 'is-active': currentSection === 'all' }"
              :aria-pressed="currentSection === 'all'"
              @click="currentSection = 'all'"
            >
              <span class="wiki-nav-icon"><v-icon size="19">mdi-view-grid-outline</v-icon></span>
              <span class="wiki-nav-copy"><strong>Wszystkie</strong><small>Cała baza wiedzy</small></span>
              <span class="wiki-nav-count">{{ articles.length }}</span>
            </button>

            <div
              v-for="s in sections"
              :key="s.sectionId"
              class="wiki-nav-row"
              :class="{ 'is-active': currentSection === s.sectionId }"
            >
              <button
                type="button"
                class="wiki-nav-item wiki-nav-item--section"
                :aria-pressed="currentSection === s.sectionId"
                @click="currentSection = s.sectionId"
              >
                <span class="wiki-nav-icon"><v-icon size="19">{{ s.icon || 'mdi-folder-outline' }}</v-icon></span>
                <span class="wiki-nav-copy"><strong>{{ s.label }}</strong><small>{{ articleCountFor(s.sectionId) }} artykułów</small></span>
                <span class="wiki-nav-count">{{ articleCountFor(s.sectionId) }}</span>
              </button>
              <button
                v-if="canManage"
                type="button"
                class="wiki-nav-delete"
                :title="$t('wiki.deleteSection')"
                :aria-label="$t('wiki.deleteSection') + ': ' + s.label"
                @click.stop="confirmDeleteSection(s)"
              ><v-icon size="15">mdi-trash-can-outline</v-icon></button>
            </div>
          </div>

          <div class="wiki-nav-divider"></div>
          <button
            type="button"
            class="wiki-nav-item wiki-nav-item--files"
            :class="{ 'is-active': currentSection === '__files' }"
            :aria-pressed="currentSection === '__files'"
            @click="currentSection = '__files'"
          >
            <span class="wiki-nav-icon"><v-icon size="19">mdi-folder-multiple-outline</v-icon></span>
            <span class="wiki-nav-copy"><strong>Pliki zespołu</strong><small>Foldery i notatki</small></span>
            <v-icon size="17" class="wiki-nav-arrow">mdi-chevron-right</v-icon>
          </button>
        </aside>

        <section class="wiki-content" :class="{ 'wiki-content--files': currentSection === '__files' }" aria-label="Zawartość Wiki">
          <wiki-files-browser v-if="currentSection === '__files'" :can-manage="canManage" />

          <template v-else>
            <div class="wiki-content-head">
              <div>
                <div class="wiki-content-eyebrow">{{ normalizedQuery ? 'Wyniki wyszukiwania' : 'Biblioteka dokumentów' }}</div>
                <h2>{{ activeSectionTitle }}</h2>
                <p>{{ activeSectionDescription }}</p>
              </div>
              <div class="wiki-results-count">
                <strong>{{ filteredArticles.length }}</strong>
                <span>{{ filteredArticles.length === 1 ? 'wynik' : 'wyników' }}</span>
              </div>
            </div>

            <div v-if="loading" class="wiki-state">
              <div class="wiki-state-orb"><v-progress-circular indeterminate size="30" width="3" color="#7896FF" /></div>
              <div class="wiki-state-title">Ładuję bazę wiedzy</div>
              <div class="wiki-state-sub">Pobieram najnowsze artykuły i sekcje.</div>
            </div>

            <div v-else-if="filteredArticles.length === 0" class="wiki-state">
              <div class="wiki-state-orb"><v-icon size="36">{{ normalizedQuery ? 'mdi-file-search-outline' : 'mdi-book-open-blank-variant' }}</v-icon></div>
              <div class="wiki-state-title">{{ normalizedQuery ? 'Brak pasujących materiałów' : $t('wiki.noArticles') }}</div>
              <div class="wiki-state-sub">{{ normalizedQuery ? 'Spróbuj krótszej frazy albo wybierz inną sekcję.' : $t('wiki.noArticlesHint') }}</div>
              <button v-if="normalizedQuery" type="button" class="wiki-action wiki-action--secondary wiki-state-action" @click="query = ''">
                <v-icon size="17">mdi-backspace-outline</v-icon>Wyczyść wyszukiwanie
              </button>
              <button v-else-if="canManage" type="button" class="wiki-action wiki-action--primary wiki-state-action" @click="openCreateArticle">
                <v-icon size="17">mdi-plus</v-icon>{{ $t('wiki.addArticle') }}
              </button>
            </div>

            <div v-else class="wiki-grid">
              <article
                v-for="a in filteredArticles"
                :key="a.id"
                class="wiki-card"
                :style="{ '--wiki-file-color': typeColor(a.mainFileType) }"
              >
                <button type="button" class="wiki-card-open" @click="openArticle(a)">
                  <div class="wiki-card-accent"></div>
                  <div class="wiki-card-top">
                    <span class="wiki-file-icon" aria-hidden="true"><v-icon size="24">{{ fileIcon(a.mainFileType) }}</v-icon></span>
                    <div class="wiki-card-type">
                      <span>{{ (a.mainFileType || 'FILE').toUpperCase() }}</span>
                      <small>{{ sectionLabel(a.sectionId) }}</small>
                    </div>
                  </div>

                  <h3 class="wiki-card-title">{{ a.title }}</h3>

                  <div class="wiki-card-meta">
                    <span v-if="a.attachments && a.attachments.length"><v-icon size="15">mdi-paperclip</v-icon>{{ a.attachments.length }} zał.</span>
                    <span v-if="a.mainFileSize"><v-icon size="15">mdi-database-outline</v-icon>{{ a.mainFileSize }}</span>
                    <span v-if="!a.mainFileSize && !(a.attachments && a.attachments.length)"><v-icon size="15">mdi-file-check-outline</v-icon>Gotowy do otwarcia</span>
                  </div>

                  <div class="wiki-card-footer">
                    <span>Otwórz materiał</span>
                    <span class="wiki-card-arrow"><v-icon size="18">mdi-arrow-right</v-icon></span>
                  </div>
                </button>
                <v-btn
                  v-if="canManage"
                  icon
                  small
                  class="wiki-card-del"
                  @click="confirmDeleteArticle(a)"
                  :title="$t('wiki.deleteArticle')"
                  :aria-label="$t('wiki.deleteArticle') + ': ' + a.title"
                ><v-icon size="17">mdi-trash-can-outline</v-icon></v-btn>
              </article>
            </div>
          </template>
        </section>
      </div>
    </div>

    <!-- ── Dialog: dodaj artykuł ── -->
    <v-dialog v-model="articleDialog" max-width="540" persistent>
      <v-card dark class="wiki-dialog-card">
        <v-card-title class="wiki-dialog-title"><span class="wiki-dialog-icon"><v-icon>mdi-file-plus-outline</v-icon></span><span><small>Baza wiedzy</small>{{ $t('wiki.newArticle') }}</span></v-card-title>
        <v-card-text>
          <v-text-field
            v-model="articleForm.title"
            :label="$t('wiki.articleTitle')"
            :placeholder="$t('wiki.articleTitlePlaceholder')"
            outlined dense
            autofocus
          />
          <v-select
            v-model="articleForm.sectionId"
            :items="sections.map(s => ({ text: s.label, value: s.sectionId }))"
            :label="$t('wiki.section')"
            outlined dense
            :disabled="sections.length === 0"
            :hint="sections.length === 0 ? $t('wiki.addSectionFirst') : ''"
            persistent-hint
          />
          <div class="wiki-file-pick">
            <v-btn small outlined color="primary" @click="$refs.mainFileInput.click()">
              <v-icon small left>mdi-file-pdf-box</v-icon>
              {{ articleForm.file ? $t('wiki.changeFile') : $t('wiki.selectPdf') }}
            </v-btn>
            <span v-if="articleForm.file" class="wiki-file-name">{{ articleForm.file.name }}</span>
            <input
              ref="mainFileInput"
              type="file"
              accept=".pdf,application/pdf"
              style="display:none"
              @change="onMainFilePick"
            >
          </div>
        </v-card-text>
        <v-card-actions class="wiki-dialog-actions">
          <v-spacer />
          <v-btn text :disabled="uploading" @click="articleDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="uploading"
            :disabled="!canSubmitArticle"
            @click="submitArticle"
          >{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog: nowa notatka (treść -> PDF jako artykuł) ── -->
    <v-dialog v-model="noteDialog" max-width="640" persistent>
      <v-card dark class="wiki-dialog-card">
        <v-card-title class="wiki-dialog-title"><span class="wiki-dialog-icon"><v-icon>mdi-note-plus-outline</v-icon></span><span><small>Baza wiedzy</small>Nowa notatka</span></v-card-title>
        <v-card-text>
          <v-text-field
            v-model="noteForm.title"
            label="Tytuł notatki"
            placeholder="np. Procedura zwrotu auta"
            outlined dense
            autofocus
          />
          <v-select
            v-model="noteForm.sectionId"
            :items="sections.map(s => ({ text: s.label, value: s.sectionId }))"
            :label="$t('wiki.section')"
            outlined dense
          />
          <v-textarea
            v-model="noteForm.content"
            label="Treść notatki"
            placeholder="Wpisz treść — zostanie zapisana jako czytelny PDF."
            outlined
            rows="10"
            auto-grow
          />
          <div class="text-caption grey--text">Notatka zostanie zapisana jako artykuł z plikiem PDF. Później możesz ją edytować — PDF wygeneruje się na nowo.</div>
        </v-card-text>
        <v-card-actions class="wiki-dialog-actions">
          <v-spacer />
          <v-btn text :disabled="noteSaving" @click="noteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="noteSaving"
            :disabled="!noteForm.title.trim() || !noteForm.content.trim() || !noteForm.sectionId"
            @click="submitNote"
          >{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog: nowa sekcja ── -->
    <v-dialog v-model="sectionDialog" max-width="460">
      <v-card dark class="wiki-dialog-card">
        <v-card-title class="wiki-dialog-title"><span class="wiki-dialog-icon"><v-icon>mdi-folder-plus-outline</v-icon></span><span><small>Organizacja Wiki</small>{{ $t('wiki.newSection') }}</span></v-card-title>
        <v-card-text>
          <v-text-field
            v-model="sectionForm.label"
            :label="$t('wiki.sectionName')"
            :placeholder="$t('wiki.sectionNamePlaceholder')"
            outlined dense
            autofocus
            @input="onSectionLabelInput"
          />
          <v-text-field
            v-model="sectionForm.sectionId"
            :label="$t('wiki.sectionId')"
            outlined dense
          />
          <v-select
            v-model="sectionForm.icon"
            :items="iconOptions"
            :label="$t('wiki.icon')"
            outlined dense
          >
            <template v-slot:selection="{ item }">
              <v-icon small class="mr-2">{{ item.value }}</v-icon>{{ item.text }}
            </template>
            <template v-slot:item="{ item }">
              <v-icon small class="mr-2">{{ item.value }}</v-icon>{{ item.text }}
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions class="wiki-dialog-actions">
          <v-spacer />
          <v-btn text @click="sectionDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" depressed :disabled="!sectionForm.label || !sectionForm.sectionId" @click="submitSection">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog: potwierdzenie usunięcia ── -->
    <v-dialog v-model="deleteDialog" max-width="430">
      <v-card dark class="wiki-dialog-card wiki-dialog-card--danger">
        <v-card-title class="wiki-dialog-title"><span class="wiki-dialog-icon wiki-dialog-icon--danger"><v-icon>mdi-trash-can-outline</v-icon></span><span><small>Operacja nieodwracalna</small>{{ $t('wiki.confirmation') }}</span></v-card-title>
        <v-card-text>
          {{ $t('wiki.confirmDeleteMsg') }} „{{ deleteTargetName }}"?
          <div v-if="deleteType === 'section'" class="caption mt-2 red--text">
            {{ $t('wiki.sectionDeleteWarning') }}
          </div>
        </v-card-text>
        <v-card-actions class="wiki-dialog-actions">
          <v-spacer />
          <v-btn text @click="deleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" depressed @click="executeDelete">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import WikiFilesBrowser from '@/components/WikiFilesBrowser.vue';
const axios = require('axios');
import { isSuperAdmin as isSuperAdminFn } from '@/services/permissions';

export default {
  components: { WikiFilesBrowser },
  name: 'WikiPage',

  data: () => ({
    loading: false,
    uploading: false,
    query: '',
    currentSection: 'all',

    sections: [],
    articles: [],

    articleDialog: false,
    noteDialog: false,
    noteSaving: false,
    noteForm: { title: '', sectionId: '', content: '' },
    articleForm: { title: '', sectionId: '', file: null },

    sectionDialog: false,
    sectionForm: { sectionId: '', label: '', icon: 'mdi-folder-outline' },

    deleteDialog: false,
    deleteTarget: null,
    deleteType: null,

    iconOptions: [
      { text: 'Folder', value: 'mdi-folder-outline' },
      { text: 'Dokument', value: 'mdi-file-document-outline' },
      { text: 'Umowa', value: 'mdi-file-sign' },
      { text: 'Firma', value: 'mdi-domain' },
      { text: 'Samochód', value: 'mdi-car' },
      { text: 'Kierowca', value: 'mdi-account-tie' },
      { text: 'Info', value: 'mdi-information-outline' },
      { text: 'Instrukcja', value: 'mdi-book-open-page-variant' },
      { text: 'Finanse', value: 'mdi-cash-multiple' },
      { text: 'Ustawienia', value: 'mdi-cog-outline' },
    ],
  }),

  computed: {
    canManage() {
      if (isSuperAdminFn()) return true;
      try {
        const edits = JSON.parse(localStorage.getItem('pagesWithEdit') || '[]');
        return Array.isArray(edits) && edits.includes('wiki');
      } catch (e) { return false; }
    },
    normalizedQuery() {
      return this.query.trim();
    },
    filteredArticles() {
      const q = this.normalizedQuery.toLowerCase();
      let list = this.articles;
      if (this.currentSection !== 'all') {
        list = list.filter(a => a.sectionId === this.currentSection);
      }
      if (q) {
        list = list.filter(a => (a.title || '').toLowerCase().includes(q));
      }
      return list;
    },
    attachmentTotal() {
      return this.articles.reduce((total, article) => (
        total + (Array.isArray(article.attachments) ? article.attachments.length : 0)
      ), 0);
    },
    activeSectionTitle() {
      if (this.normalizedQuery) return `Wyniki dla „${this.normalizedQuery}”`;
      if (this.currentSection === 'all') return 'Wszystkie materiały';
      const section = this.sections.find(item => item.sectionId === this.currentSection);
      return section ? section.label : 'Materiały';
    },
    activeSectionDescription() {
      if (this.normalizedQuery) return 'Materiały dopasowane do wpisanej frazy i wybranej sekcji.';
      if (this.currentSection === 'all') return 'Pełny katalog instrukcji, procedur i dokumentów dostępnych w BTP.';
      return 'Dokumenty i instrukcje przypisane do tego obszaru wiedzy.';
    },
    canSubmitArticle() {
      const f = this.articleForm;
      return !!(f.title && f.title.trim() && f.sectionId && f.file);
    },
    deleteTargetName() {
      if (!this.deleteTarget) return '';
      return this.deleteTarget.title || this.deleteTarget.label || this.deleteTarget.name || '';
    },
  },

  mounted() {
    this.fetchAll();
  },

  methods: {
    authHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    apiUrl(path) {
      return process.env.VUE_APP_ROOT_API + path;
    },
    async fetchAll() {
      this.loading = true;
      try {
        const [s, a] = await Promise.all([
          axios({ method: 'get', url: this.apiUrl('wiki/sections'), headers: this.authHeaders() }),
          axios({ method: 'get', url: this.apiUrl('wiki/articles'), headers: this.authHeaders() }),
        ]);
        this.sections = s.data || [];
        this.articles = a.data || [];
      } catch (e) {
        console.error('Wiki fetch failed:', e);
      }
      this.loading = false;
    },

    // ── Sekcje ──
    sectionLabel(sectionId) {
      const s = this.sections.find(x => x.sectionId === sectionId);
      return s ? s.label : (sectionId || '—');
    },
    articleCountFor(sectionId) {
      return this.articles.filter(a => a.sectionId === sectionId).length;
    },
    openCreateSection() {
      this.sectionForm = { sectionId: '', label: '', icon: 'mdi-folder-outline' };
      this.sectionDialog = true;
    },
    onSectionLabelInput() {
      if (this.sectionForm.sectionId && this.sectionForm.sectionId !== this.slugify(this.prevLabel || '')) return;
      this.prevLabel = this.sectionForm.label;
      this.sectionForm.sectionId = this.slugify(this.sectionForm.label);
    },
    slugify(text) {
      return (text || '')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40);
    },
    async submitSection() {
      try {
        await axios({
          method: 'post',
          url: this.apiUrl('wiki/sections'),
          headers: this.authHeaders(),
          data: {
            sectionId: this.sectionForm.sectionId,
            label: this.sectionForm.label,
            icon: this.sectionForm.icon,
          },
        });
        this.sectionDialog = false;
        await this.fetchAll();
      } catch (e) {
        console.error('Section create failed:', e);
        alert(e.response?.data?.message || 'Nie udało się utworzyć sekcji');
      }
    },
    confirmDeleteSection(s) {
      this.deleteTarget = s;
      this.deleteType = 'section';
      this.deleteDialog = true;
    },

    // ── Artykuły ──
    typeColor(type) {
      const c = { pdf: '#FF7373', docx: '#64B5F6', xlsx: '#42D787', img: '#A78BFA' };
      return c[type] || '#8EA7FF';
    },
    fileIcon(type) {
      const icons = {
        pdf: 'mdi-file-pdf-box',
        docx: 'mdi-file-word-box',
        xlsx: 'mdi-file-excel-box',
        img: 'mdi-file-image-outline',
      };
      return icons[type] || 'mdi-file-document-outline';
    },
    openCreateArticle() {
      if (this.sections.length === 0) {
        alert('Najpierw dodaj sekcję (np. „Umowy").');
        this.openCreateSection();
        return;
      }
      const defaultSection = this.currentSection !== 'all'
        ? this.currentSection
        : this.sections[0].sectionId;
      this.articleForm = { title: '', sectionId: defaultSection, file: null };
      this.articleDialog = true;
    },
    onMainFilePick(e) {
      const f = e.target.files && e.target.files[0];
      if (f) this.articleForm.file = f;
      e.target.value = '';
    },
    openCreateNote() {
      if (this.sections.length === 0) {
        alert('Najpierw dodaj sekcję (np. „Umowy").');
        this.openCreateSection();
        return;
      }
      const defaultSection = this.currentSection !== 'all' && this.currentSection !== '__files'
        ? this.currentSection
        : this.sections[0].sectionId;
      this.noteForm = { title: '', sectionId: defaultSection, content: '' };
      this.noteDialog = true;
    },
    async submitNote() {
      this.noteSaving = true;
      try {
        const res = await axios.post(
          this.apiUrl('wiki/articles/note'),
          {
            title: this.noteForm.title.trim(),
            sectionId: this.noteForm.sectionId,
            content: this.noteForm.content,
          },
          { headers: this.authHeaders() },
        );
        this.noteDialog = false;
        await this.fetchAll();
        if (res.data && res.data.id) this.openArticle(res.data);
      } catch (e) {
        console.error('Note create failed:', e);
        alert(e.response?.data?.message || 'Nie udało się utworzyć notatki');
      }
      this.noteSaving = false;
    },
    async submitArticle() {
      if (!this.canSubmitArticle) return;
      this.uploading = true;
      try {
        const fd = new FormData();
        fd.append('file', this.articleForm.file);
        fd.append('title', this.articleForm.title.trim());
        fd.append('sectionId', this.articleForm.sectionId);
        const res = await axios({
          method: 'post',
          url: this.apiUrl('wiki/articles'),
          data: fd,
          headers: { ...this.authHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        this.articleDialog = false;
        await this.fetchAll();
        if (res.data && res.data.id) this.openArticle(res.data);
      } catch (e) {
        console.error('Article upload failed:', e);
        alert(e.response?.data?.message || 'Nie udało się utworzyć artykułu');
      }
      this.uploading = false;
    },
    openArticle(a) {
      if (!a || !a.id) return;
      this.$router.push({ name: 'WikiArticlePage', params: { id: a.id } }).catch(() => {});
    },
    confirmDeleteArticle(a) {
      this.deleteTarget = a;
      this.deleteType = 'article';
      this.deleteDialog = true;
    },

    // ── Usuwanie ──
    async executeDelete() {
      if (!this.deleteTarget) return;
      try {
        const path = this.deleteType === 'section'
          ? 'wiki/sections/' + this.deleteTarget.id
          : 'wiki/articles/' + this.deleteTarget.id;
        await axios({
          method: 'delete',
          url: this.apiUrl(path),
          headers: this.authHeaders(),
        });
        if (this.deleteType === 'section' && this.currentSection === this.deleteTarget.sectionId) {
          this.currentSection = 'all';
        }
        this.deleteDialog = false;
        this.deleteTarget = null;
        this.deleteType = null;
        await this.fetchAll();
      } catch (e) {
        console.error('Delete failed:', e);
        alert(e.response?.data?.message || 'Nie udało się usunąć');
      }
    },
  },
};
</script>

<style scoped>
.wiki {
  --wiki-bg: #171B26;
  --wiki-surface: #232838;
  --wiki-surface-soft: #1F2434;
  --wiki-surface-hover: #292F40;
  --wiki-border: rgba(255, 255, 255, 0.075);
  --wiki-border-strong: rgba(126, 151, 255, 0.24);
  --wiki-text: #F5F7FC;
  --wiki-muted: #AEB8CA;
  --wiki-subtle: #8B96AA;
  --wiki-blue: #5B7CFF;
  --wiki-green: #42D787;
  --wiki-purple: #A78BFA;
  min-height: calc(100vh - 56px);
  margin: -24px;
  padding: 24px clamp(16px, 2.2vw, 34px) 52px;
  background:
    radial-gradient(circle at 7% -8%, rgba(91, 124, 255, 0.17), transparent 29%),
    radial-gradient(circle at 93% 3%, rgba(167, 139, 250, 0.08), transparent 25%),
    var(--wiki-bg);
  color: var(--wiki-text);
}
.wiki * { box-sizing: border-box; }
.wiki-shell { width: 100%; max-width: 1680px; margin: 0 auto; }

/* Nagłówek */
.wiki-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 2px 2px 20px;
}
.wiki-heading { display: flex; min-width: 0; align-items: center; gap: 14px; }
.wiki-heading-icon {
  display: flex;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(142, 167, 255, 0.3);
  border-radius: 17px;
  background: linear-gradient(145deg, #4168FF, #755EF0);
  box-shadow: 0 18px 34px -18px rgba(80, 107, 255, 0.9);
}
.wiki-heading-copy { min-width: 0; }
.wiki-eyebrow {
  color: #91A0B7;
  font-size: 10.5px;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}
.wiki-heading h1 {
  margin: 3px 0 0;
  color: #FFFFFF;
  font-size: clamp(25px, 2.5vw, 32px);
  font-weight: 900;
  letter-spacing: -0.045em;
  line-height: 1.05;
}
.wiki-heading p { max-width: 680px; margin: 7px 0 0; color: var(--wiki-muted); font-size: 13px; line-height: 1.45; }
.wiki-header-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 9px; }
.wiki-action {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.055);
  color: #E8ECF4;
  font: inherit;
  font-size: 12px;
  font-weight: 750;
  cursor: pointer;
  transition: transform 0.17s ease, background 0.17s ease, border-color 0.17s ease, box-shadow 0.17s ease;
}
.wiki-action .v-icon { color: inherit !important; }
.wiki-action:hover { border-color: rgba(255, 255, 255, 0.2); background: rgba(255, 255, 255, 0.085); transform: translateY(-1px); }
.wiki-action--primary { border-color: #5B7CFF; background: #5B7CFF; color: #fff; box-shadow: 0 12px 26px -16px rgba(91, 124, 255, 0.95); }
.wiki-action--primary:hover { border-color: #6D89FF; background: #6D89FF; }
.wiki-action--secondary { color: #CDD4E2; }

/* Wyszukiwarka i liczniki */
.wiki-command {
  display: grid;
  grid-template-columns: minmax(320px, 1fr) auto;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid var(--wiki-border);
  border-radius: 18px;
  background: rgba(31, 36, 52, 0.82);
  box-shadow: 0 20px 48px -38px rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(14px);
}
.wiki-search-wrap {
  display: flex;
  min-width: 0;
  min-height: 62px;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid var(--wiki-border);
  border-radius: 14px;
  background: #191E2B;
  cursor: text;
  transition: border-color 0.17s ease, box-shadow 0.17s ease, background 0.17s ease;
}
.wiki-search-wrap:focus-within { border-color: rgba(126, 151, 255, 0.55); background: #1C2230; box-shadow: 0 0 0 3px rgba(91, 124, 255, 0.11); }
.wiki-search-icon {
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: rgba(91, 124, 255, 0.13);
}
.wiki-search-icon .v-icon { color: #7896FF !important; }
.wiki-search-copy { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 2px; }
.wiki-search-copy small { color: var(--wiki-subtle); font-size: 10px; font-weight: 750; letter-spacing: 0.065em; text-transform: uppercase; }
.wiki-search-input {
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--wiki-text);
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}
.wiki-search-input::placeholder { color: #929DAF; font-weight: 500; }
.wiki-search-input::-webkit-search-cancel-button { display: none; }
.wiki-search-clear {
  display: flex;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.055);
  color: #AEB8CA;
  cursor: pointer;
}
.wiki-search-clear:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
.wiki-search-shortcut { padding: 5px 8px; border: 1px solid var(--wiki-border); border-radius: 8px; color: #8995A9; font-size: 10.5px; font-weight: 700; white-space: nowrap; }
.wiki-stats { display: grid; grid-template-columns: repeat(3, minmax(112px, 1fr)); gap: 8px; }
.wiki-stat { display: flex; min-width: 120px; align-items: center; gap: 9px; padding: 9px 11px; border: 1px solid var(--wiki-border); border-radius: 13px; background: rgba(35, 40, 56, 0.72); }
.wiki-stat-icon { display: flex; width: 35px; height: 35px; flex: 0 0 35px; align-items: center; justify-content: center; border-radius: 10px; }
.wiki-stat-icon--blue { background: rgba(91, 124, 255, 0.13); }
.wiki-stat-icon--blue .v-icon { color: #7896FF !important; }
.wiki-stat-icon--purple { background: rgba(167, 139, 250, 0.13); }
.wiki-stat-icon--purple .v-icon { color: #B69DFF !important; }
.wiki-stat-icon--green { background: rgba(66, 215, 135, 0.12); }
.wiki-stat-icon--green .v-icon { color: #58DE96 !important; }
.wiki-stat strong { display: block; color: #F8FAFF; font-size: 17px; font-weight: 850; line-height: 1; }
.wiki-stat small { display: block; margin-top: 4px; color: var(--wiki-subtle); font-size: 10.5px; line-height: 1; }

/* Przestrzeń robocza */
.wiki-workspace { display: grid; grid-template-columns: 264px minmax(0, 1fr); align-items: start; gap: 16px; }
.wiki-navigation {
  position: sticky;
  top: 16px;
  padding: 14px;
  border: 1px solid var(--wiki-border);
  border-radius: 18px;
  background: rgba(31, 36, 52, 0.9);
  box-shadow: 0 20px 50px -42px rgba(0, 0, 0, 0.9);
}
.wiki-nav-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 4px 5px 13px; }
.wiki-nav-head span { display: block; color: var(--wiki-subtle); font-size: 9.5px; font-weight: 750; letter-spacing: 0.08em; text-transform: uppercase; }
.wiki-nav-head strong { display: block; margin-top: 3px; color: #E7EBF3; font-size: 13px; }
.wiki-nav-total { min-width: 27px; padding: 4px 7px; border-radius: 999px; background: rgba(91, 124, 255, 0.13); color: #8EA7FF !important; text-align: center; }
.wiki-nav-list { display: flex; flex-direction: column; gap: 5px; }
.wiki-nav-row { position: relative; min-width: 0; }
.wiki-nav-item {
  display: flex;
  width: 100%;
  min-height: 54px;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border: 1px solid transparent;
  border-radius: 13px;
  background: transparent;
  color: var(--wiki-muted);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: color 0.16s ease, background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
}
.wiki-nav-item:hover { border-color: rgba(255, 255, 255, 0.07); background: rgba(255, 255, 255, 0.04); color: #E9EDF5; }
.wiki-nav-item.is-active,
.wiki-nav-row.is-active .wiki-nav-item { border-color: rgba(104, 136, 255, 0.26); background: linear-gradient(90deg, rgba(91, 124, 255, 0.2), rgba(91, 124, 255, 0.075)); color: #fff; box-shadow: inset 3px 0 0 #6F8CFF; }
.wiki-nav-item--section { padding-right: 38px; }
.wiki-nav-icon { display: flex; width: 34px; height: 34px; flex: 0 0 34px; align-items: center; justify-content: center; border-radius: 10px; background: rgba(255, 255, 255, 0.055); }
.wiki-nav-icon .v-icon { color: #8F9AAF !important; }
.wiki-nav-item.is-active .wiki-nav-icon,
.wiki-nav-row.is-active .wiki-nav-icon { background: rgba(91, 124, 255, 0.24); }
.wiki-nav-item.is-active .wiki-nav-icon .v-icon,
.wiki-nav-row.is-active .wiki-nav-icon .v-icon { color: #9AB0FF !important; }
.wiki-nav-copy { min-width: 0; flex: 1; }
.wiki-nav-copy strong { display: block; overflow: hidden; color: inherit; font-size: 12.5px; font-weight: 750; text-overflow: ellipsis; white-space: nowrap; }
.wiki-nav-copy small { display: block; margin-top: 2px; overflow: hidden; color: #929DAF; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.wiki-nav-row.is-active .wiki-nav-copy small,
.wiki-nav-item.is-active .wiki-nav-copy small { color: #9EABD0; }
.wiki-nav-count { min-width: 27px; padding: 3px 6px; border-radius: 999px; background: rgba(255, 255, 255, 0.055); color: #8D98AA; font-size: 10px; font-weight: 800; text-align: center; }
.wiki-nav-delete {
  position: absolute;
  top: 13px;
  right: 7px;
  display: flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #7D879A;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.16s ease, color 0.16s ease, background 0.16s ease;
}
.wiki-nav-row:hover .wiki-nav-delete,
.wiki-nav-delete:focus-visible { opacity: 1; }
.wiki-nav-delete:hover { background: rgba(255, 115, 115, 0.12); color: #FF8B8B; }
.wiki-nav-row .wiki-nav-count { margin-right: 24px; }
.wiki-nav-divider { height: 1px; margin: 11px 4px; background: var(--wiki-border); }
.wiki-nav-item--files { background: rgba(91, 124, 255, 0.055); }
.wiki-nav-arrow { color: #707B90 !important; }

.wiki-content {
  min-width: 0;
  min-height: 560px;
  padding: 18px;
  border: 1px solid var(--wiki-border);
  border-radius: 18px;
  background: rgba(31, 36, 52, 0.72);
  box-shadow: 0 22px 54px -44px rgba(0, 0, 0, 0.9);
}
.wiki-content--files { min-height: 0; padding: 0; border-color: transparent; background: transparent; box-shadow: none; }
.wiki-content-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 18px; margin-bottom: 17px; padding: 1px 2px 16px; border-bottom: 1px solid var(--wiki-border); }
.wiki-content-eyebrow { color: #7F92D7; font-size: 9.5px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.wiki-content-head h2 { margin: 4px 0 0; color: #F7F9FD; font-size: clamp(20px, 2vw, 25px); font-weight: 850; letter-spacing: -0.035em; }
.wiki-content-head p { max-width: 700px; margin: 6px 0 0; color: var(--wiki-subtle); font-size: 11.5px; line-height: 1.45; }
.wiki-results-count { display: flex; flex: 0 0 auto; align-items: baseline; gap: 5px; padding: 8px 11px; border: 1px solid var(--wiki-border); border-radius: 11px; background: #1B202D; }
.wiki-results-count strong { color: #F4F6FB; font-size: 16px; }
.wiki-results-count span { color: var(--wiki-subtle); font-size: 10.5px; }

/* Karty artykułów */
.wiki-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(278px, 1fr)); gap: 12px; }
.wiki-card {
  --wiki-file-color: #8EA7FF;
  position: relative;
  display: flex;
  min-height: 218px;
  flex-direction: column;
  overflow: hidden;
  padding: 15px;
  border: 1px solid var(--wiki-border);
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(39, 45, 62, 0.92), rgba(30, 35, 49, 0.96));
  cursor: pointer;
  outline: none;
  transition: transform 0.17s ease, border-color 0.17s ease, box-shadow 0.17s ease, background 0.17s ease;
}
.wiki-card-accent { position: absolute; top: 0; right: 22px; left: 22px; height: 2px; background: linear-gradient(90deg, transparent, var(--wiki-file-color), transparent); opacity: 0.62; }
.wiki-card:hover,
.wiki-card:focus-within { z-index: 1; border-color: color-mix(in srgb, var(--wiki-file-color) 45%, transparent); background: linear-gradient(145deg, rgba(42, 49, 68, 0.97), rgba(31, 37, 52, 0.99)); box-shadow: 0 18px 34px -26px color-mix(in srgb, var(--wiki-file-color) 58%, transparent); transform: translateY(-2px); }
.wiki-card-open { display: flex; width: 100%; min-height: 186px; flex: 1 1 auto; flex-direction: column; padding: 0; border: 0; outline: 0; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.wiki-card-open:focus-visible { outline: 2px solid #8EA7FF; outline-offset: 6px; border-radius: 8px; }
.wiki-card-top { display: flex; align-items: center; gap: 10px; padding-right: 34px; }
.wiki-file-icon { display: flex; width: 44px; height: 44px; flex: 0 0 44px; align-items: center; justify-content: center; border: 1px solid color-mix(in srgb, var(--wiki-file-color) 30%, transparent); border-radius: 13px; background: color-mix(in srgb, var(--wiki-file-color) 13%, transparent); }
.wiki-file-icon .v-icon { color: var(--wiki-file-color) !important; }
.wiki-card-type { min-width: 0; flex: 1; }
.wiki-card-type span { display: block; color: var(--wiki-file-color); font-size: 9.5px; font-weight: 850; letter-spacing: 0.1em; }
.wiki-card-type small { display: block; margin-top: 3px; overflow: hidden; color: #8F9AAF; font-size: 10.5px; text-overflow: ellipsis; white-space: nowrap; }
.wiki-card-del { position: absolute !important; top: 12px; right: 12px; color: #778195 !important; opacity: 0; transition: opacity 0.16s ease, color 0.16s ease, background 0.16s ease; }
.wiki-card:hover .wiki-card-del,
.wiki-card-del:focus-visible { opacity: 1; }
.wiki-card-del:hover { background: rgba(255, 115, 115, 0.11) !important; color: #FF8B8B !important; }
.wiki-card-title { display: -webkit-box; min-height: 48px; margin: 17px 0 12px; overflow: hidden; color: #F5F7FC; font-size: 16px; font-weight: 780; letter-spacing: -0.015em; line-height: 1.42; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow-wrap: anywhere; }
.wiki-card-meta { display: flex; min-height: 22px; align-items: center; gap: 10px; flex-wrap: wrap; color: #8C97AA; font-size: 10.5px; }
.wiki-card-meta span { display: inline-flex; align-items: center; gap: 4px; }
.wiki-card-meta .v-icon { color: #768297 !important; }
.wiki-card-footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: auto; padding-top: 13px; border-top: 1px solid var(--wiki-border); color: #AEB7C7; font-size: 10.5px; font-weight: 750; }
.wiki-card-arrow { display: flex; width: 30px; height: 30px; align-items: center; justify-content: center; border-radius: 9px; background: rgba(91, 124, 255, 0.1); }
.wiki-card-arrow .v-icon { color: #7896FF !important; transition: transform 0.17s ease; }
.wiki-card:hover .wiki-card-arrow .v-icon { transform: translateX(2px); }

/* Stany */
.wiki-state { display: flex; min-height: 390px; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 54px 20px; color: var(--wiki-muted); text-align: center; }
.wiki-state-orb { display: flex; width: 72px; height: 72px; align-items: center; justify-content: center; margin-bottom: 5px; border: 1px solid rgba(126, 151, 255, 0.2); border-radius: 22px; background: linear-gradient(145deg, rgba(91, 124, 255, 0.16), rgba(167, 139, 250, 0.08)); color: #7896FF; }
.wiki-state-orb .v-icon { color: #7896FF !important; }
.wiki-state-title { color: var(--wiki-text); font-size: 16px; font-weight: 780; }
.wiki-state-sub { max-width: 360px; color: var(--wiki-subtle); font-size: 12px; line-height: 1.45; }
.wiki-state-action { margin-top: 10px; }

/* Dialogi */
.wiki-dialog-card { overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.09) !important; border-radius: 18px !important; background: #232838 !important; color: #F5F7FC !important; box-shadow: 0 26px 70px rgba(5, 8, 18, 0.55) !important; }
.wiki-dialog-title { display: flex !important; align-items: center; gap: 12px; padding: 19px 21px 16px !important; border-bottom: 1px solid rgba(255, 255, 255, 0.075); color: #F5F7FC !important; font-size: 17px !important; font-weight: 780 !important; }
.wiki-dialog-title > span:last-child { display: flex; flex-direction: column; }
.wiki-dialog-title small { margin-bottom: 2px; color: #8B96AA; font-size: 9px; font-weight: 750; letter-spacing: 0.08em; text-transform: uppercase; }
.wiki-dialog-icon { display: flex; width: 39px; height: 39px; align-items: center; justify-content: center; border-radius: 12px; background: rgba(91, 124, 255, 0.14); }
.wiki-dialog-icon .v-icon { color: #7896FF !important; }
.wiki-dialog-icon--danger { background: rgba(255, 115, 115, 0.12); }
.wiki-dialog-icon--danger .v-icon { color: #FF8B8B !important; }
.wiki-dialog-card ::v-deep .v-card__text { padding: 21px !important; color: #B9C2D2 !important; }
.wiki-dialog-card ::v-deep .v-label { color: #8F9AAF !important; }
.wiki-dialog-card ::v-deep input,
.wiki-dialog-card ::v-deep textarea,
.wiki-dialog-card ::v-deep .v-select__selection { color: #F2F5FA !important; }
.wiki-dialog-card ::v-deep .v-input__slot { background: #1B202D !important; }
.wiki-dialog-card ::v-deep fieldset { border-color: rgba(255, 255, 255, 0.11) !important; }
.wiki-dialog-actions { padding: 12px 18px 16px !important; border-top: 1px solid rgba(255, 255, 255, 0.075); }
.wiki-file-pick { display: flex; align-items: center; gap: 10px; margin-top: 4px; }
.wiki-file-name { overflow: hidden; color: #D5DBE7; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }

.wiki-action:focus-visible,
.wiki-search-clear:focus-visible,
.wiki-nav-item:focus-visible,
.wiki-nav-delete:focus-visible { outline: 2px solid #8EA7FF; outline-offset: 2px; }

@media (max-width: 1180px) {
  .wiki-command { grid-template-columns: 1fr; }
  .wiki-stats { grid-template-columns: repeat(3, 1fr); }
  .wiki-stat { min-width: 0; }
}

@media (max-width: 960px) {
  .wiki-workspace { grid-template-columns: 1fr; }
  .wiki-navigation { position: static; padding: 11px; overflow: hidden; }
  .wiki-nav-head { display: none; }
  .wiki-nav-list { flex-direction: row; overflow-x: auto; padding: 1px 1px 7px; scrollbar-width: thin; }
  .wiki-nav-row { flex: 0 0 auto; }
  .wiki-nav-item { width: auto; min-width: 150px; min-height: 50px; }
  .wiki-nav-item--section { padding-right: 36px; }
  .wiki-nav-copy small { display: none; }
  .wiki-nav-count { margin-left: auto; }
  .wiki-nav-row .wiki-nav-count { margin-right: 22px; }
  .wiki-nav-divider { display: none; }
  .wiki-nav-item--files { width: 100%; margin-top: 5px; }
  .wiki-nav-delete { opacity: 1; }
}

@media (max-width: 680px) {
  .wiki { margin: -16px; padding: 18px 14px 38px; }
  .wiki-header { align-items: flex-start; flex-direction: column; gap: 16px; padding-bottom: 17px; }
  .wiki-heading { align-items: flex-start; }
  .wiki-heading-icon { width: 46px; height: 46px; flex-basis: 46px; border-radius: 15px; }
  .wiki-heading p { max-width: 470px; }
  .wiki-header-actions { width: 100%; }
  .wiki-header-actions .wiki-action { flex: 1; }
  .wiki-command { padding: 9px; border-radius: 16px; }
  .wiki-search-wrap { min-height: 58px; }
  .wiki-search-shortcut { display: none; }
  .wiki-stats { gap: 6px; }
  .wiki-stat { justify-content: center; padding: 8px 6px; }
  .wiki-stat-icon { width: 31px; height: 31px; flex-basis: 31px; }
  .wiki-stat strong { font-size: 15px; }
  .wiki-stat small { font-size: 9px; }
  .wiki-navigation { border-radius: 16px; }
  .wiki-nav-item { min-width: 138px; }
  .wiki-content { min-height: 470px; padding: 14px; border-radius: 16px; }
  .wiki-content-head { align-items: flex-start; }
  .wiki-content-head p { display: none; }
  .wiki-results-count { padding: 7px 9px; }
  .wiki-grid { grid-template-columns: 1fr; }
  .wiki-card { min-height: 204px; }
  .wiki-card-del { opacity: 1; }
  .wiki-file-pick { align-items: flex-start; flex-direction: column; }
}

@media (max-width: 410px) {
  .wiki-heading p { font-size: 12px; }
  .wiki-header-actions { flex-direction: column-reverse; }
  .wiki-header-actions .wiki-action { width: 100%; }
  .wiki-stat { flex-direction: column; gap: 4px; text-align: center; }
  .wiki-stat small { margin-top: 2px; }
  .wiki-content-head h2 { font-size: 19px; }
  .wiki-results-count span { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .wiki *,
  .wiki *::before,
  .wiki *::after { scroll-behavior: auto !important; transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
}
</style>
